import React, {useEffect, useMemo, useCallback} from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { Flex, FlexColumn } from 'src/components/flex';
import { EPropertyFormField, type PropertyForm } from 'src/components/create-or-update-property-modal/types';

import styles from 'src/components/create-or-update-property-modal/PropertyForm.module.css';
import Select from 'src/components/input/select/Select';
import NumberField from 'src/components/input/number-field/NumberField';
import SearchAddressField from 'src/components/input/address/SearchAddressField';
import AddressFields from 'src/components/input/address/AddressFields';
import SelectLabels from 'src/components/input/select-labels/SelectLabels';
import {type IProperty, useProperties} from 'src/services/api';
import Autocomplete from '@mui/material/Autocomplete';
import {PropertyType, propertyTypeChildToParent, propertyTypeOptions} from 'src/api/landlord-properties-api';

type Props = {
    form: UseFormReturn<PropertyForm>;
    onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void> | void;
};

const SELECTABLE_COUNTRIES = [
    {
        label: 'United States',
        value: 'United States',
    },
    {
        label: 'Canada',
        value: 'Canada',
    }
];


const PropertyIcon = (property: IProperty) => {
    switch (property.type) {
        case 'Apartment Building':
        case 'Apartment Floor Plan':
            return <img src="/img/properties/apartment_building.svg" alt="svg" />;
        case 'Condo':
        case 'Apartment':
            return <img src="/img/properties/condo.svg" alt="svg" />;
        case 'House':
            return <img src="/img/properties/house.svg" alt="svg" />;
        case 'Room':
            return <img src="/img/properties/room.svg" alt="svg" />;
        case 'Townhouse':
            return <img src="/img/properties/townhouse.svg" alt="svg" />;
        case 'Community':
            return <img src="/img/properties/community.svg" alt="svg" />;
        case null:
            return null;
    }
};

export default function PropertyForm({
    form,
    onSubmit,
}: Props) {
    const { register, setValue, watch, formState: { errors } } = form;

    const isVacant = watch(EPropertyFormField.IsVacant);
    const isIncomeCapped = watch(EPropertyFormField.IsIncomeCapped);
    const name = watch(EPropertyFormField.Name);
    const address = watch(EPropertyFormField.Address);
    const country = watch(EPropertyFormField.Country);
    const propertyType = watch(EPropertyFormField.Type);
    const parentPropertyId = watch(EPropertyFormField.ParentProperty);

    const { data: properties, isLoading: propertiesLoading } = useProperties();

    const parentPropertyOptions = useMemo(() => {
        if (!properties || propertiesLoading || !propertyType) {
            return [];
        }

        return properties
            .filter((property) => {
                // Handle old code where propertyType is not defined
                if (property.type == null) {
                    return false; // Show all properties if no type is selected
                }
                const parentTypes = propertyTypeChildToParent[propertyType];
                return parentTypes ? Boolean(parentTypes.includes(property.type)) : false;
            })
            .map((property) => property.id);
    }, [properties, propertiesLoading, propertyType]);


    useEffect(() => {
        if (propertyType === PropertyType.Community || propertyType === PropertyType.ApartmentBuilding) {
            setValue(EPropertyFormField.SquareFeet, undefined, { shouldValidate: false });
            setValue(EPropertyFormField.NumberOfBathrooms, '', { shouldValidate: false });
            setValue(EPropertyFormField.NumberOfBedrooms, '', { shouldValidate: false });
        }
        if (propertyType === PropertyType.Community) {
            setValue(EPropertyFormField.ParentProperty, null, { shouldValidate: false });
        }
    }, [propertyType, setValue]);

    const showSqFeetAndRooms = ![PropertyType.Community, PropertyType.ApartmentBuilding].includes(propertyType as PropertyType);
    const isRoomRoomsRequired = propertyType !== PropertyType.Room;

    const parentPropertyOptionsMap = useMemo<Record<number, IProperty>>(() => {
        if (!properties || propertiesLoading) {
            return {};
        }

        return properties.reduce((acc, property) => {
            acc[property.id] = property;
            return acc;
        }, {} as Record<number, IProperty>);
    }, [properties, propertiesLoading]);

    const fillAddressFromParent = useCallback(() => {
        if (!parentPropertyId || !parentPropertyOptionsMap[parentPropertyId]) {
            return;
        }

        const parentProperty = parentPropertyOptionsMap[parentPropertyId];
        if (!parentProperty) {
            return;
        }

        if (parentProperty?.country || parentProperty?.street_address || parentProperty?.city || parentProperty?.state || parentProperty?.zipcode) {
            setValue(EPropertyFormField.Country, parentProperty.country || '', { shouldValidate: true, shouldDirty: true });
            setValue(EPropertyFormField.Address, {
                streetAddress: parentProperty.street_address || '',
                unit: parentProperty.unit || null,
                city: parentProperty.city || '',
                state: parentProperty.state || '',
                zipcode: parentProperty.zipcode || '',
            }, { shouldValidate: true, shouldDirty: true });
            // Set address.state individually so the already-mounted Controller re-renders
            form.setValue('address.state', (parentProperty.state || '') as never, { shouldValidate: true, shouldDirty: true });
        }
    }, [parentPropertyId, setValue, parentPropertyOptionsMap]);


    const childAddressEqualToParent = useMemo<boolean>(() => {
        if (!parentPropertyId || !parentPropertyOptionsMap[parentPropertyId] || !address) {
            return false;
        }

        const parentProperty = parentPropertyOptionsMap[parentPropertyId];
        if (!parentProperty) {
            return false;
        }

        return (
            parentProperty.street_address === address.streetAddress &&
            parentProperty.unit === address.unit &&
            parentProperty.city === address.city &&
            parentProperty.state === address.state &&
            parentProperty.zipcode === address.zipcode
        );
    }, [address?.streetAddress, address?.unit, address?.city, address?.state, address?.zipcode, parentPropertyId, parentPropertyOptionsMap]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={onSubmit}>
                <FlexColumn rowGap={2}>
                    <Flex alignItems="center" columnGap={2} className={styles.vacancyRow}>
                        <FormControlLabel
                            className={styles.vacancyToggle}
                            labelPlacement="end"
                            control={
                                <Switch
                                    checked={!!isVacant}
                                    onChange={(_, checked) =>
                                        setValue(EPropertyFormField.IsVacant, checked, { shouldValidate: true, shouldDirty: true })
                                    }
                                    size="small"
                                    data-testid="is-vacant-switch"
                                />
                            }
                            label={<Typography variant="body2" noWrap>Vacant</Typography>}
                        />
                        <div className={styles.vacancyDate}>
                            {isVacant && (
                                <Controller
                                    name={EPropertyFormField.VacantSince}
                                    control={form.control}
                                    rules={{ required: 'Field is required' }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Vacant since *"
                                            value={field.value}
                                            onChange={(date) => setValue(EPropertyFormField.VacantSince, date, { shouldValidate: true, shouldDirty: true })}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                    error: !!errors[EPropertyFormField.VacantSince],
                                                    helperText: errors[EPropertyFormField.VacantSince]?.message,
                                                    InputLabelProps: {shrink: true}
                                                }
                                            }}
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </Flex>

                    <TextField
                        {...register(EPropertyFormField.Name)}
                        label="Property name"
                        error={!!errors[EPropertyFormField.Name]}
                        helperText={
                            errors[EPropertyFormField.Name]?.message ||
                            (!name && 'If the property name is not specified, it will be automatically generated based on the address.')
                        }
                        size="small"
                        fullWidth
                        data-testid="property-name-field"
                        InputLabelProps={{shrink: true}}
                    />

                    <Controller
                        name={EPropertyFormField.Type}
                        control={form.control}
                        rules={{
                            required: 'Type is required',
                        }}
                        render={({ field }) => (
                            <Select
                                label="Type *"
                                options={propertyTypeOptions}
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                error={!!errors[EPropertyFormField.Type]}
                                helperText={errors[EPropertyFormField.Type]?.message}
                                size="small"
                                fullWidth
                                data-testid="property-type-select"
                            />
                        )}
                    />

                    {propertyType != null && <>
                        <Flex justifyContent="space-between" >
                            {propertyType != PropertyType.Community &&
                            <Controller
                                name={EPropertyFormField.ParentProperty}
                                control={form.control}
                                render={({field}) => (
                                    <Autocomplete
                                        fullWidth
                                        disabled={!parentPropertyOptions.length}
                                        onChange={(_v, option) => field.onChange(option)}
                                        options={parentPropertyOptions}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Parent property"
                                                error={!!errors[EPropertyFormField.ParentProperty]}
                                                helperText={errors[EPropertyFormField.ParentProperty]?.message}
                                                size="small"
                                                fullWidth
                                                data-testid="parent-property-field"
                                                InputLabelProps={{...params.InputLabelProps, shrink: true}}
                                            />
                                        )}
                                        value={field.value ?? null}
                                        getOptionLabel={(option) => {
                                            return parentPropertyOptionsMap[option]?.name || '';
                                        }}
                                        renderOption={({ key, ...props}, option) => {
                                            const property = parentPropertyOptionsMap[option];
                                            return <Tooltip title={property.type} key={key} followCursor>
                                                <li data-testid={`parent-property-option-${property.id}`} {...props}>
                                                    <Flex sx={{ minWidth: 0 }}>
                                                        <PropertyIcon {...property} /> {' '}
                                                        <span className="ellipsisText" style={{ minWidth: 0, flex: 1 }}>
                                                            {property.name || 'No name'}
                                                        </span>
                                                    </Flex>
                                                </li>
                                            </Tooltip>;
                                        }}
                                    />)}
                            />}
                            {parentPropertyId && (
                                <Button
                                    sx={{width: '250px'}}
                                    variant="contained"
                                    size="small"
                                    onClick={fillAddressFromParent}
                                    disabled={childAddressEqualToParent}
                                >{childAddressEqualToParent ? 'Address matches parent' : 'Fill address from parent'}</Button>)}
                        </Flex>

                        <Controller
                            name={EPropertyFormField.Labels}
                            control={form.control}
                            render={({field}) => (
                                <SelectLabels
                                    type="property"
                                    label="Labels"
                                    value={field.value}
                                    onChange={(value) => field.onChange(value)}
                                    error={!!errors[EPropertyFormField.Labels]}
                                    helperText={errors[EPropertyFormField.Labels]?.message}
                                    size="small"
                                    fullWidth
                                    data-testid="labels-select"
                                />
                            )}
                        />

                        <fieldset className={styles.addressGroup}>
                            <legend>Address</legend>

                            <Controller
                                name={EPropertyFormField.Country}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        label="Country *"
                                        options={SELECTABLE_COUNTRIES}
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value);

                                            if (value === 'Canada') {
                                                form.setValue(EPropertyFormField.Address, {
                                                    streetAddress: '',
                                                    unit: '',
                                                    city: '',
                                                    state: '',
                                                    zipcode: '',
                                                });
                                                return;
                                            }

                                            if (value === 'United States') {
                                                form.setValue(EPropertyFormField.Address, null);
                                                return;
                                            }
                                        }}
                                        error={!!errors[EPropertyFormField.Country]}
                                        helperText={errors[EPropertyFormField.Country]?.message}
                                        size="small"
                                        fullWidth
                                        data-testid="country-select"
                                    />
                                )}
                            />

                            {(!address && country === 'United States') && (
                                <Controller
                                    name={EPropertyFormField.Address}
                                    control={form.control}
                                    rules={{
                                        required: 'Address is required',
                                    }}
                                    render={({field}) => (
                                        <SearchAddressField
                                            onChange={(newAddress) => field.onChange(newAddress)}
                                            error={!!errors[EPropertyFormField.Address]}
                                            helperText={errors[EPropertyFormField.Address]?.message}
                                            data-testid="address-field"
                                        />
                                    )}
                                />
                            )}

                            {!!address && (
                                <AddressFields
                                    country={country}
                                    form={form}
                                    value={address}
                                    errors={errors[EPropertyFormField.Address]}
                                />
                            )}
                        </fieldset>

                        <FlexColumn rowGap={2}>
                            <Flex alignItems="center" columnGap={2}>
                                <NumberField
                                    {...register(EPropertyFormField.Rent, {
                                        min: {
                                            value: 1,
                                            message: 'Rent must be greater than 0',
                                        },
                                        max: {
                                            value: 1000000,
                                            message: 'Rent must be less than 1,000,000',
                                        }
                                    })}
                                    type="number"
                                    label="Rent"
                                    error={!!errors[EPropertyFormField.Rent]}
                                    helperText={errors[EPropertyFormField.Rent]?.message}
                                    size="small"
                                    fullWidth
                                    data-testid="rent-field"
                                    InputLabelProps={{shrink: true}}
                                />
                                <FormControlLabel
                                    labelPlacement="end"
                                    sx={{ flexShrink: 0, mr: 0 }}
                                    control={
                                        <Switch
                                            checked={!!isIncomeCapped}
                                            onChange={(_, checked) =>
                                                setValue(EPropertyFormField.IsIncomeCapped, checked, { shouldValidate: true, shouldDirty: true })
                                            }
                                            size="small"
                                            data-testid="is-income-capped-switch"
                                        />
                                    }
                                    label={<Typography variant="body2" noWrap>Income capped (affordable housing)</Typography>}
                                />
                            </Flex>

                            {showSqFeetAndRooms && <>
                                {isRoomRoomsRequired && <Flex columnGap={2}>
                                    <NumberField
                                        {...register(
                                            EPropertyFormField.NumberOfBedrooms,
                                            {
                                                required: 'Number of bedrooms is required',
                                                min: {
                                                    value: 0,
                                                    message: 'Number of bedrooms must be greater or equal to 0',
                                                },
                                                max: {
                                                    value: 100,
                                                    message: 'Number of bedrooms must be less than 100',
                                                }
                                            }
                                        )}
                                        type="number"
                                        label="Number of bedrooms *"
                                        error={!!errors[EPropertyFormField.NumberOfBedrooms]}
                                        helperText={errors[EPropertyFormField.NumberOfBedrooms]?.message}
                                        size="small"
                                        fullWidth
                                        data-testid="numb-bedrooms-field"
                                        InputLabelProps={{shrink: true}}
                                    />

                                    <NumberField
                                        {...register(
                                            EPropertyFormField.NumberOfBathrooms,
                                            {
                                                required: 'Number of bathrooms is required',
                                                min: {
                                                    value: 0,
                                                    message: 'Number of bathrooms must be greater or equal to 0',
                                                },
                                                max: {
                                                    value: 100,
                                                    message: 'Number of bathrooms must be less than 100',
                                                }
                                            }
                                        )}
                                        type="number"
                                        label="Number of bathrooms *"
                                        error={!!errors[EPropertyFormField.NumberOfBathrooms]}
                                        helperText={errors[EPropertyFormField.NumberOfBathrooms]?.message}
                                        size="small"
                                        fullWidth
                                        data-testid="numb-bathrooms-field"
                                        InputLabelProps={{shrink: true}}
                                    />
                                </Flex>}

                                <NumberField
                                    {...register(
                                        EPropertyFormField.SquareFeet,
                                        {
                                            min: {
                                                value: 0,
                                                message: 'Square feet must be greater than 0',
                                            },
                                            max: {
                                                value: 1000000,
                                                message: 'Square feet must be less than 1,000,000',
                                            }
                                        }
                                    )}
                                    type="number"
                                    label="Square feet"
                                    error={!!errors[EPropertyFormField.SquareFeet]}
                                    helperText={errors[EPropertyFormField.SquareFeet]?.message}
                                    size="small"
                                    fullWidth
                                    data-testid="square-feet-field"
                                    InputLabelProps={{shrink: true}}
                                />
                            </>}
                        </FlexColumn>
                    </>}

                    <TextField
                        {...register(EPropertyFormField.Notes)}
                        label="Notes"
                        placeholder="Write your notes here"
                        error={!!errors[EPropertyFormField.Notes]}
                        helperText={errors[EPropertyFormField.Notes]?.message}
                        multiline
                        rows={4}
                        size="small"
                        fullWidth
                        data-testid="property-notes-field"
                        InputLabelProps={{shrink: true}}
                    />
                </FlexColumn>
            </form>
        </LocalizationProvider>
    );
}
